import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ModalOverlay } from '@/components/ui/modalOverlay';

interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  iconName?: IconName;
  tone?: 'default' | 'danger';
  isConfirming?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  cancelLabel,
  iconName,
  tone = 'default',
  isConfirming = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const isDanger = tone === 'danger';
  const resolvedIconName: IconName = iconName === undefined ? 'AlertCircle' : iconName;
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <ModalOverlay onRequestClose={onCancel} initialFocusRef={confirmButtonRef}>
      <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-7 text-center">
        <div
          className={
            'w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ' +
            (isDanger ? 'bg-zooerrorbg' : 'bg-zoovpalelavender')
          }
        >
          <Icon
            name={resolvedIconName}
            className={'text-xl ' + (isDanger ? 'text-zooerrortext' : 'text-zooprimary')}
          />
        </div>
        <h2 className="text-lg font-bold text-zooindigo">{title}</h2>
        <p className="text-sm text-zoomuted mt-1.5">{description}</p>
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            disabled={isConfirming}
            onClick={onCancel}
            className="flex-1 py-3 border border-zooborder bg-zoowhite text-zooindigo text-sm font-bold rounded-xl transition-colors hover:bg-zoovpalelavender disabled:opacity-60 disabled:pointer-events-none"
          >
            {cancelLabel === undefined ? t('actions.cancel') : cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            disabled={isConfirming}
            aria-busy={isConfirming === true ? true : undefined}
            onClick={onConfirm}
            className={
              'flex-1 py-3 flex items-center justify-center gap-2 text-zoowhite text-sm font-bold rounded-xl shadow-md transition-colors disabled:opacity-60 ' +
              (isDanger
                ? 'bg-zooerrortext hover:bg-zoodangerhover'
                : 'bg-zooprimary hover:bg-zooprimaryhover')
            }
          >
            {isConfirming === true ? (
              <LoadingSpinner className="w-4 h-4 border-zoowhite border-t-transparent" />
            ) : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
